/**
 * Profit Scenarios Section
 *
 * Rendered inside the Revenue Target tab: current-reality baseline with
 * "what it takes" sensitivities, an interactive scenario builder, and a
 * comparison table of saved scenarios against the target margin.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useProfitScenarios } from '@/hooks/useProfitScenarios';
import { formatCurrency } from '@/utils/formatters';
import { ScenarioBuilder } from './ScenarioBuilder';
import type { LoadedScenario } from './ScenarioBuilder';
import type { ScenarioInput } from '@/types/profitScenario';
import './ProfitScenarios.css';

const FEASIBLE_UTILISATION_MAX = 100;

interface ProfitScenariosSectionProps {
  /** Increment to force a refetch (e.g. after the target config is saved) */
  refreshToken?: number;
}

export function ProfitScenariosSection({
  refreshToken = 0,
}: ProfitScenariosSectionProps) {
  const {
    data, loading, error, mutating, mutateError,
    refresh, createScenario, updateScenario, deleteScenario,
  } = useProfitScenarios();

  const [loaded, setLoaded] = useState<LoadedScenario | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const clearLoaded = useCallback(() => setLoaded(null), []);

  // Refetch when the parent signals the target config changed —
  // the target margin drives every computed result in this section.
  const lastToken = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken !== lastToken.current) {
      lastToken.current = refreshToken;
      refresh();
    }
  }, [refreshToken, refresh]);

  // Keep the loaded snapshot in sync after a successful update so a
  // second edit starts from what was actually persisted.
  const handleUpdate = useCallback(
    async (id: string, input: ScenarioInput): Promise<boolean> => {
      const ok = await updateScenario(id, input);
      if (ok) {
        setLoaded({
          id,
          name: input.name,
          description: input.description,
          drivers: input.drivers,
        });
      }
      return ok;
    },
    [updateScenario]
  );

  // Two-step delete; only eject the loaded scenario once the delete
  // actually succeeded.
  const handleDelete = useCallback(
    async (id: string): Promise<void> => {
      setConfirmingDeleteId(null);
      const ok = await deleteScenario(id);
      if (ok && loaded?.id === id) {
        setLoaded(null);
      }
    },
    [deleteScenario, loaded]
  );

  if (loading && !data) {
    return (
      <section className="rt-section" aria-label="Profit scenarios">
        <h3 className="rt-section__title">Profit scenarios</h3>
        <p className="ps-muted">Loading scenario model...</p>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="rt-section" aria-label="Profit scenarios">
        <h3 className="rt-section__title">Profit scenarios</h3>
        <p className="ps-error" role="alert">{error}</p>
        <button onClick={refresh} className="ps-retry" type="button">
          Try again
        </button>
      </section>
    );
  }

  if (!data) return null;

  const { baseline, scenarios, targetMarginPercent } = data;
  const wit = baseline.whatItTakes;
  const utilisationFeasible =
    wit.utilisationForTargetPercent !== null &&
    wit.utilisationForTargetPercent <= FEASIBLE_UTILISATION_MAX;

  return (
    <section className="rt-section ps-section" aria-label="Profit scenarios">
      <h3 className="rt-section__title">Profit scenarios</h3>
      <p className="rt-section__note">
        Model how utilisation, rates, product sales and cost cuts move net
        profit. Costs are seeded live from Xero; capacity and rates from
        Scoro. Baseline: {Math.round(baseline.drivers.utilisationPercent)}%
        utilisation at ${baseline.drivers.averageHourlyRate}/h across{' '}
        {baseline.drivers.billableHeadcount} billable people.
      </p>

      {/* What it takes to hit the target */}
      <div className="ps-wit" aria-label="What it takes to hit the target">
        <span className="ps-wit__title">
          Paths to {targetMarginPercent}% NPBT (single lever, monthly):
        </span>
        <div className="ps-wit__chips">
          {wit.utilisationForTargetPercent !== null && (
            <span className={`ps-chip ${utilisationFeasible ? '' : 'ps-chip--infeasible'}`}>
              Utilisation → {wit.utilisationForTargetPercent.toFixed(0)}%
              {utilisationFeasible ? '' : ' (over capacity)'}
            </span>
          )}
          {wit.rateForTarget !== null && (
            <span className="ps-chip">
              Avg rate → {formatCurrency(wit.rateForTarget)}/h
            </span>
          )}
          {wit.productRevenueForTarget !== null && (
            <span className="ps-chip">
              Product sales → +{formatCurrency(wit.productRevenueForTarget)}/mo extra
            </span>
          )}
          {wit.costReductionForTarget !== null && (
            <span className="ps-chip">
              Cost cut → {formatCurrency(wit.costReductionForTarget)}/mo
            </span>
          )}
          {wit.breakEvenUtilisationPercent !== null && (
            <span className="ps-chip ps-chip--muted">
              Break-even utilisation: {wit.breakEvenUtilisationPercent.toFixed(0)}%
            </span>
          )}
        </div>
        <p className="ps-wit__hint">
          Each +1pt utilisation ≈ {formatCurrency(wit.revenuePerUtilisationPoint)}/mo;
          each +$1 on the rate ≈ {formatCurrency(wit.revenuePerRateDollar)}/mo.
        </p>
      </div>

      {/* Builder */}
      <ScenarioBuilder
        baselineDrivers={baseline.drivers}
        targetMarginPercent={targetMarginPercent}
        labourModel={baseline.labourModel}
        loadedScenario={loaded}
        mutating={mutating}
        mutateError={mutateError}
        onCreate={createScenario}
        onUpdate={handleUpdate}
        onClearLoaded={clearLoaded}
      />

      {/* Saved scenarios */}
      {scenarios.length > 0 && (
        <div className="ps-saved">
          <h4 className="ps-saved__title">Saved scenarios</h4>
          <div className="rt-table-wrap">
            <table className="rt-table">
              <thead>
                <tr>
                  <th scope="col">Scenario</th>
                  <th scope="col">Utilisation</th>
                  <th scope="col">Rate</th>
                  <th scope="col">Revenue /mo</th>
                  <th scope="col">NPBT /mo</th>
                  <th scope="col">Margin</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="ps-baseline-row">
                  <td>Baseline (today)</td>
                  <td>{Math.round(baseline.drivers.utilisationPercent)}%</td>
                  <td>${baseline.drivers.averageHourlyRate}</td>
                  <td>{formatCurrency(baseline.result.totalRevenueMonthly)}</td>
                  <td className={baseline.result.npbtMonthly < 0 ? 'rt-negative' : ''}>
                    {formatCurrency(baseline.result.npbtMonthly)}
                  </td>
                  <td>
                    <span className={`ps-margin ${baseline.result.meetsTarget ? 'ps-margin--good' : 'ps-margin--bad'}`}>
                      {baseline.result.npbtMarginPercent.toFixed(1)}%
                    </span>
                  </td>
                  <td></td>
                </tr>
                {scenarios.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>{Math.round(s.drivers.utilisationPercent)}%</td>
                    <td>${s.drivers.averageHourlyRate}</td>
                    <td>{formatCurrency(s.result.totalRevenueMonthly)}</td>
                    <td className={s.result.npbtMonthly < 0 ? 'rt-negative' : ''}>
                      {formatCurrency(s.result.npbtMonthly)}
                    </td>
                    <td>
                      <span className={`ps-margin ${s.result.meetsTarget ? 'ps-margin--good' : 'ps-margin--bad'}`}>
                        {s.result.npbtMarginPercent.toFixed(1)}%
                      </span>
                    </td>
                    <td className="ps-actions">
                      <button
                        onClick={() => setLoaded({
                          id: s.id,
                          name: s.name,
                          description: s.description,
                          drivers: s.drivers,
                        })}
                        className="ps-action-btn"
                        type="button"
                        aria-label={`Load scenario ${s.name}`}
                      >
                        Load
                      </button>
                      {confirmingDeleteId === s.id ? (
                        <>
                          <button
                            onClick={() => handleDelete(s.id)}
                            className="ps-action-btn ps-action-btn--danger"
                            type="button"
                            aria-label={`Confirm delete of scenario ${s.name}`}
                          >
                            Confirm delete
                          </button>
                          <button
                            onClick={() => setConfirmingDeleteId(null)}
                            className="ps-action-btn"
                            type="button"
                            aria-label="Cancel delete"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setConfirmingDeleteId(s.id)}
                          className="ps-action-btn ps-action-btn--danger"
                          type="button"
                          aria-label={`Delete scenario ${s.name}`}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {mutateError && (
            <p className="ps-error" role="alert">{mutateError}</p>
          )}
        </div>
      )}
    </section>
  );
}
